/* Native product models: prepare after critical page readiness; create WebGL on activation. */
(() => {
  if (customElements.get('product-native-3d-preview')) return;
  let runtime;
  const modelBytes = new Map();
  const downloadModel = url => {
    if (modelBytes.has(url)) return modelBytes.get(url);
    const record = { controller: new AbortController() };
    record.promise = fetch(url, { signal: record.controller.signal }).then(response => {
      if (!response.ok) throw new Error('Model download failed');
      return response.blob();
    }).then(blob => { record.blob = blob; return blob; }).catch(error => {
      if (modelBytes.get(url) === record) modelBytes.delete(url);
      throw error;
    });
    modelBytes.set(url, record);
    while (modelBytes.size > 3) {
      const [key, oldest] = modelBytes.entries().next().value;
      modelBytes.delete(key);
      if (!oldest.blob) oldest.controller.abort();
    }
    return record;
  };
  const feature = (name, version) => new Promise((resolve, reject) => {
    if (!window.Shopify?.loadFeatures) return reject(new Error('Shopify unavailable'));
    Shopify.loadFeatures([{ name, version, onLoad: errors => errors ? reject(errors) : resolve() }]);
  });
  const loadRuntime = () => runtime ||= (async () => {
    const css = new Promise((resolve, reject) => {
      let link = document.querySelector('link[data-product-native-css]');
      if (link?.sheet) return resolve();
      if (link) link.remove();
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
      link.dataset.productNativeCss = '';
      link.onload = resolve;
      link.onerror = () => { link.remove(); reject(new Error('Viewer styles unavailable')); };
      document.head.append(link);
    });
    await Promise.all([css, feature('model-viewer', '1.12'), feature('model-viewer-ui', '1.0')]);
    await customElements.whenDefined('model-viewer');
  })().catch(error => { runtime = undefined; throw error; });
  customElements.define('product-native-3d-preview', class extends HTMLElement {
    connectedCallback() {
      this.launch = this.querySelector('.product-3d-open');
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.product-3d-stage');
      this.status = this.querySelector('[role="status"]');
      this.retry = this.querySelector('.product-3d-retry');
      this.token = 0;
      try { this.models = JSON.parse(this.querySelector('[data-model-map]').textContent); } catch { this.models = {}; }
      this.productTitle = this.dataset.productTitle || 'Product';
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.launch.addEventListener('click', () => this.open(), options);
      this.querySelector('.product-3d-close').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.cleanup(), options);
      this.retry.addEventListener('click', () => { this.track('retry'); this.retryToken = Date.now(); this.load(); }, options);
      if (typeof subscribe === 'function') {
        this.unsubscribeSelection = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (data?.event?.target && this.closest('product-info')?.contains(data.event.target)) {
            this.pendingVariant = true;
            this.launch.disabled = true;
            if (this.desktopAction) this.desktopAction.querySelector('button').disabled = true;
            if (this.mobileAction) this.mobileAction.querySelector('button').disabled = true;
            if (this.dialog.open) this.close();
          }
        });
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== this.dataset.section) return;
          this.dataset.variantId = String(data.variant?.id || '');
          this.dataset.selectedFinish = this.dataset.finishIndex !== '' ? (data.variant?.options?.[Number(this.dataset.finishIndex)] || '') : '';
          this.pendingVariant = false;
          this.launch.disabled = !data.variant;
          this.updateFinishLabel();
          this.placeDesktopAction();
        });
      }
      this.hidden = false;
      this.updateFinishLabel();
      this.placeDesktopAction();
      this.setupWarmup();
    }
    setupWarmup() {
      const options = { signal: this.events.signal };
      const schedule = () => this.scheduleWarmup();
      window.addEventListener('load', schedule, options);
      document.addEventListener('visibilitychange', schedule, options);
      this.closest('media-gallery')?.addEventListener('load', schedule, { ...options, capture: true });
      this.launch.addEventListener('pointerenter', () => this.prepare(), options);
      this.launch.addEventListener('focus', () => this.prepare(), options);
      if ('IntersectionObserver' in window) {
        this.nearbyObserver = new IntersectionObserver(schedule, { rootMargin: '200px' });
        this.nearbyObserver.observe(this.launch);
        if (this.desktopAction) this.nearbyObserver.observe(this.desktopAction);
        if (this.mobileAction) this.nearbyObserver.observe(this.mobileAction);
      }
      this.scheduleWarmup();
    }
    canPrepare() {
      const connection = navigator.connection;
      if (document.readyState !== 'complete' || document.visibilityState !== 'visible' || this.pendingVariant || this.hidden || !this.currentModel || this.dialog.open || connection?.saveData || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')) return false;
      const photo = this.closest('media-gallery')?.querySelector('[id^="GalleryViewer"] li img');
      if (!photo?.complete || !photo.naturalWidth) return false;
      return [this.launch, this.desktopAction, this.mobileAction].some(element => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.bottom > 0 && rect.top < innerHeight + 200;
      });
    }
    scheduleWarmup() {
      clearTimeout(this.warmTimer);
      if (!this.canPrepare()) return;
      this.warmTimer = setTimeout(() => {
        if ('requestIdleCallback' in window) this.warmIdle = requestIdleCallback(() => this.prepare());
        else this.prepare(); // Safari fallback: delayed after full load, not a claim of idle time.
      }, 'requestIdleCallback' in window ? 650 : 1200);
    }
    prepare() {
      if (!this.canPrepare() || this.preparedModel === this.currentModel) return;
      const url = this.currentModel;
      this.preparedModel = url;
      this.preparationTiming = { model: url, start: performance.now(), ready: null };
      this.warmDownload = this.modelRecord?.selfContained ? downloadModel(url) : null;
      Promise.all([loadRuntime(), this.warmDownload?.promise]).then(() => {
        if (this.preparedModel === url) this.preparationTiming.ready = performance.now();
      }).catch(() => {
        if (this.preparedModel === url) this.preparedModel = undefined;
      });
    }
    updateFinishLabel() {
      const note = this.querySelector('.product-3d-fallback');
      const selected = this.dataset.selectedFinish || 'current selection';
      const record = this.models[this.dataset.variantId];
      let nextModel = '';
      try {
        const url = new URL(record?.url?.startsWith('//') ? `https:${record.url}` : (record?.url || ''), location.href);
        if (record?.url && url.protocol === 'https:') nextModel = url.href;
      } catch { /* Invalid model references stay unavailable. */ }
      this.hidden = !nextModel;
      this.launch.disabled = !nextModel;
      this.modelRecord = record;
      if (this.currentModel && this.currentModel !== nextModel) {
        if (this.warmDownload && !this.warmDownload.blob) this.warmDownload.controller.abort();
        this.preparedModel = undefined;
        this.dispose();
        this.stage.replaceChildren();
        this.retryToken = undefined;
      }
      this.currentModel = nextModel;
      this.displayedFinish = record?.finish || 'Finish unspecified';
      note.hidden = selected === this.displayedFinish; 
      note.textContent = `Selected finish: ${selected}. This 3D view shows ${this.displayedFinish} so you can explore the shape.`;
      this.querySelector('.product-3d-model-finish').textContent = `${this.displayedFinish} · 3D preview`;
      this.scheduleWarmup();
    }
    placeDesktopAction() {
      const gallery = this.closest('media-gallery');
      if (!gallery) return;
      for (const old of [this.desktopAction, this.mobileAction]) {
        if (old) this.nearbyObserver?.unobserve(old);
        old?.remove();
      }
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const hero = viewer?.querySelector('li');
      if (!hero || this.hidden) return;
      const makeAction = className => {
        const action = this.querySelector('.product-3d-action').cloneNode(true);
        action.classList.add(className);
        action.querySelector('button').addEventListener('click', () => this.open(className.includes('desktop') ? 'desktop_gallery' : 'mobile_gallery'), { signal: this.events.signal });
        action.addEventListener('pointerenter', () => this.prepare(), { signal: this.events.signal });
        action.addEventListener('focusin', () => this.prepare(), { signal: this.events.signal });
        this.nearbyObserver?.observe(action);
        return action;
      };
      this.desktopAction = makeAction('product-3d-desktop-action');
      viewer.append(this.desktopAction);
      this.mobileAction = makeAction('product-3d-mobile-action');
      viewer.append(this.mobileAction);
    }
    track(action, extra = {}) {
      const gallery = this.closest('media-gallery');
      const payload = {
        action,
        product_id: gallery?.dataset.productId || '',
        product_handle: gallery?.dataset.productHandle || '',
        variant_id: this.dataset.variantId || '',
        selected_finish: this.dataset.selectedFinish || '',
        displayed_finish: this.displayedFinish || '',
        is_finish_fallback: (this.dataset.selectedFinish || '') !== this.displayedFinish,
        placement: this.openPlacement || (innerWidth >= 750 ? 'desktop_gallery' : 'mobile_gallery'),
        ...extra,
      };
      try {
        const published = window.Shopify?.analytics?.publish?.('aestara:product_3d_interaction', payload);
        published?.catch?.(() => {});
      } catch { /* Analytics must never interrupt the viewer. */ }
      try { window.clarity?.('event', `product_3d_${action}`); } catch { /* Consent or blockers may disable tracking. */ }
      try {
        if (typeof window.gtag === 'function') window.gtag('event', 'product_3d_interaction', payload);
        else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'product_3d_interaction', ...payload });
      } catch { /* Existing analytics only; never initialise a tracker here. */ }
    }
    trackReady(viewer) {
      const session = this.openSession;
      const check = () => {
        if (!this.isConnected || !this.dialog.open || this.viewer !== viewer || session !== this.openSession || this.openEvents.has('ready')) return;
        if (!viewer.loaded || !viewer.modelIsVisible) { requestAnimationFrame(check); return; }
        this.openEvents.add('ready');
        this.track('ready', { load_duration_ms: Math.round(performance.now() - this.loadStarted), time_to_ready_ms: Math.round(performance.now() - this.openStarted) });
      };
      requestAnimationFrame(check);
    }
    open(placement) {
      if (this.hidden || this.pendingVariant || this.launch.disabled || this.dialog.open) return;
      this.openPlacement = placement || (innerWidth >= 750 ? 'desktop_gallery' : 'mobile_gallery');
      this.openStarted = performance.now();
      this.loadStarted = this.openStarted;
      this.openSession = (this.openSession || 0) + 1;
      this.openEvents = new Set();
      this.analyticsOpen = true;
      this.focusReturn = document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.track('open');
      this.querySelector('.product-3d-close').focus();
      if (this.viewer?.loaded && this.ui) {
        this.ui.play();
        this.trackReady(this.viewer);
        this.status.textContent = 'Drag to rotate · Pinch or scroll to zoom';
      } else this.load();
    }
    async load() {
      this.loadStarted = performance.now();
      this.dispose();
      this.attemptFailed = false;
      const token = ++this.token;
      const current = () => this.isConnected && this.dialog.open && token === this.token && !this.hidden;
      this.retry.hidden = true;
      this.status.textContent = 'Preparing 3D view…';
      const galleryPhoto = this.closest('media-gallery')?.querySelector('[id^="GalleryViewer"] li img');
      const fallback = this.dataset.selectedFinish !== this.displayedFinish;
      const poster = fallback ? this.modelRecord.poster : (galleryPhoto?.currentSrc || this.modelRecord.poster);
      const photo = document.createElement('img');
      if (poster) photo.src = poster;
      photo.alt = `${this.productTitle} in ${this.displayedFinish} — product photograph`;
      this.stage.replaceChildren(...(poster ? [photo] : []));
      this.timer = setTimeout(() => { if (current()) this.fail(); }, 25000);
      try {
        const modelUrl = new URL(this.currentModel);
        if (this.retryToken) modelUrl.searchParams.set('retry', this.retryToken);
        // Shared, bounded bytes-only cache. No WebGL renderer is created before this click.
        const record = this.modelRecord.selfContained ? downloadModel(modelUrl.href) : null;
        this.modelFetch = record?.controller;
        const [, blob] = await Promise.all([loadRuntime(), record?.promise]);
        if (!current()) return;
        this.modelBlobUrl = blob ? URL.createObjectURL(blob) : undefined;
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        const attrs = { src: this.modelBlobUrl || modelUrl.href, poster, alt: `${this.productTitle} in ${this.displayedFinish}. Use arrow keys to rotate; scroll or pinch to zoom.`, 'camera-controls': '', 'touch-action': 'none', 'camera-orbit': '12deg 65deg auto', 'field-of-view': '30deg', reveal: 'auto', loading: 'eager', 'interaction-prompt': 'none', 'seamless-poster': '' };
        Object.entries(attrs).forEach(([key, value]) => { if (value != null) viewer.setAttribute(key, value); });
        viewer.addEventListener('load', () => {
          if (!current()) return;
          clearTimeout(this.timer);
          photo.remove();
          this.trackReady(viewer);
          this.status.textContent = 'Drag to rotate · Pinch or scroll to zoom';
        });
        viewer.addEventListener('camera-change', event => {
          if (this.dialog.open && this.viewer === viewer && event.detail?.source === 'user-interaction' && !this.openEvents.has('interact')) {
            this.openEvents.add('interact');
            this.track('interact', { interaction_type: 'rotate_or_zoom' });
          }
        });
        viewer.addEventListener('progress', event => {
          if (current() && !viewer.loaded) this.status.textContent = `Loading 3D view… ${Math.round((event.detail?.totalProgress || 0) * 100)}%`;
        });
        viewer.addEventListener('error', () => { if (this.isConnected && this.dialog.open && this.viewer === viewer) this.fail(); });
        this.stage.append(viewer);
        this.ui = new Shopify.ModelViewerUI(viewer);
        this.ui.play();
      } catch (error) { if (current()) this.fail(); }
    }
    fail() {
      if (!this.attemptFailed) { this.attemptFailed = true; this.track('error'); }
      ++this.token;
      this.dispose();
      this.status.textContent = 'The 3D view couldn’t load. Please try again or close to return to the photos.';
      this.retry.hidden = false;
    }
    dispose() {
      clearTimeout(this.timer);
      this.modelFetch?.abort();
      this.modelFetch = undefined;
      this.ui?.pause?.();
      this.ui?.destroy?.();
      this.ui = undefined;
      this.viewer?.remove();
      this.viewer = undefined;
      this.stage?.querySelectorAll('.shopify-model-viewer-ui, model-viewer').forEach(node => node.remove());
      if (this.modelBlobUrl) URL.revokeObjectURL(this.modelBlobUrl);
      this.modelBlobUrl = undefined;
    }
    close() {
      this.cleanup();
      this.dialog.close();
      if (this.focusReturn?.isConnected && !this.hidden) this.focusReturn.focus({ preventScroll: true });
    }
    cleanup() {
      if (this.analyticsOpen) { this.analyticsOpen = false; this.track('close'); }
      ++this.token;
      clearTimeout(this.timer);
      if (this.viewer?.loaded && this.ui && this.isConnected) {
        this.ui.pause();
      } else {
        this.dispose();
        this.stage.replaceChildren();
      }
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }
    disconnectedCallback() {
      this.cleanup();
      this.dispose();
      this.desktopAction?.remove();
      this.mobileAction?.remove();
      clearTimeout(this.warmTimer);
      if (this.warmIdle && 'cancelIdleCallback' in window) cancelIdleCallback(this.warmIdle);
      this.nearbyObserver?.disconnect();
      if (this.warmDownload && !this.warmDownload.blob) this.warmDownload.controller.abort();
      this.events?.abort();
      this.unsubscribeSelection?.();
      this.unsubscribeVariant?.();
    }
  });
})();
